import { useState } from 'react'

const APITester = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const addResult = (test, result) => {
    setResults(prev => [...prev, { test, result, timestamp: new Date().toISOString() }])
  }

  const testAPI = async (endpoint, method = 'GET', data = null) => {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:5000'
    const url = `${baseURL}${endpoint}`
    
    try {
      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      }
      
      if (data) {
        options.body = JSON.stringify(data)
      }
      
      const response = await fetch(url, options)
      const responseData = await response.text()
      
      return {
        status: response.status,
        statusText: response.statusText,
        data: responseData,
        success: response.ok
      }
    } catch (error) {
      return {
        status: 'ERROR',
        statusText: error.message,
        data: null,
        success: false
      }
    }
  }

  const runTests = async () => {
    setLoading(true)
    setResults([])

    // Test 1: Health check
    addResult('Health Check', await testAPI('/api/health'))
    
    // Test 2: Auth test endpoint
    addResult('Auth Test', await testAPI('/api/auth/test'))
    
    // Test 3: List users (if available)
    addResult('List Users', await testAPI('/api/users'))
    
    // Test 4: Login with admin credentials
    addResult('Admin Login', await testAPI('/api/auth/login', 'POST', {
      username: 'admin',
      password: 'admin123'
    }))

    setLoading(false)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">API Connection Tester</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Connection Tests</h2>
            <button
              onClick={runTests}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Running Tests...' : 'Run API Tests'}
            </button>
          </div>
          
          <div className="text-sm text-gray-600 mb-4">
            <p><strong>API Base URL:</strong> {import.meta.env.VITE_API_URL || 'http://localhost:5000'}</p>
            <p><strong>Test Credentials:</strong> admin / admin123</p>
          </div>
        </div>

        <div className="space-y-4">
          {results.map((result, index) => (
            <div key={index} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{result.test}</h3>
                <span className={`px-2 py-1 rounded text-sm ${
                  result.result.success 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {result.result.status}
                </span>
              </div>
              
              <div className="text-sm text-gray-600 mb-2">
                <strong>Status:</strong> {result.result.statusText}
              </div>
              
              {result.result.data && (
                <div className="bg-gray-50 rounded p-3">
                  <strong className="text-sm">Response:</strong>
                  <pre className="text-xs mt-1 whitespace-pre-wrap break-all">
                    {typeof result.result.data === 'string' 
                      ? result.result.data 
                      : JSON.stringify(result.result.data, null, 2)
                    }
                  </pre>
                </div>
              )}
              
              <div className="text-xs text-gray-500 mt-2">
                {result.timestamp}
              </div>
            </div>
          ))}
        </div>

        {results.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Click "Run API Tests" to test the backend connection
          </div>
        )}
      </div>
    </div>
  )
}

export default APITester